import {
  ChartContainer,
  type ChartConfig,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@taiga-platform/ui";
import { Link } from "react-router";
import { Cell, Legend, Pie, PieChart, Tooltip } from "recharts";
import { Callout, Details } from "../components";

const PIE_COLORS = [
  "var(--primary)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

function OwnershipChart({
  title,
  data,
}: {
  title: string;
  data: { name: string; value: number }[];
}) {
  const config: ChartConfig = Object.fromEntries(
    data.map((d, i) => [
      d.name,
      { label: d.name, color: PIE_COLORS[i % PIE_COLORS.length] },
    ]),
  );

  return (
    <div className="not-prose my-6">
      <h3 className="text-sm font-medium text-center text-muted-foreground mb-4">
        {title}
      </h3>
      <ChartContainer config={config} className="mx-auto max-h-[300px]">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            label={({ value }: { value: number }) => `${value}%`}
            labelLine={false}
          >
            {data.map((_, i) => (
              <Cell
                key={i}
                fill={PIE_COLORS[i % PIE_COLORS.length]}
                stroke="none"
              />
            ))}
          </Pie>
          <Tooltip
            isAnimationActive={false}
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const { name, value } = payload[0].payload;
              return (
                <div className="rounded-lg border bg-card px-3 py-1.5 text-sm shadow-md">
                  <span className="font-medium">{name}</span>
                  <span className="ml-2 text-muted-foreground">{value}%</span>
                </div>
              );
            }}
          />
          <Legend
            verticalAlign="bottom"
            formatter={(value: string) => (
              <span className="text-xs text-foreground">{value}</span>
            )}
          />
        </PieChart>
      </ChartContainer>
    </div>
  );
}

export function CapTable() {
  return (
    <>
      <h1>Cap Table</h1>

      <h2>Current Ownership</h2>
      <p>
        Four founders hold equal shares through their personal holding
        companies. One advisor angel holds 0.5% (first CLA converted). There
        is no employee option pool at this stage.
      </p>

      <table>
        <thead>
          <tr>
            <th>Stakeholder</th>
            <th>Ownership</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Founder 1 (CEO)</td>
            <td>24.875%</td>
          </tr>
          <tr>
            <td>Founder 2 (CTO)</td>
            <td>24.875%</td>
          </tr>
          <tr>
            <td>Founder 3 (Chair &amp; CCO)</td>
            <td>24.875%</td>
          </tr>
          <tr>
            <td>Founder 4 (CAIO)</td>
            <td>24.875%</td>
          </tr>
          <tr>
            <td>Advisor angel (converted)</td>
            <td>0.500%</td>
          </tr>
          <tr>
            <td>
              <strong>Total issued</strong>
            </td>
            <td>
              <strong>100.0%</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <OwnershipChart
        title="Current Ownership"
        data={[
          { name: "Founders (4x equal)", value: 99.5 },
          { name: "Advisor Angel", value: 0.5 },
        ]}
      />

      <hr />

      <h2>Outstanding Convertible Instruments</h2>
      <p>
        In addition to the 0.5% already on the cap table, TAIGA has issued
        Convertible Capital Loan Agreements (CLAs) to advisor angels under{" "}
        <Link to="/legal#advisor-angel-agreements">standardized terms</Link>{" "}
        prepared by Hannes Snellman Attorneys. All CLAs carry identical
        terms — the only variable is the loan amount. The same advisor
        holding 0.5% in shares also holds an additional CLA. An internal
        allocation of 5-10% total angel ownership (including the 0.5%
        already issued) is planned for current and upcoming CLA holders.
        The{" "}
        <Link to="/legal#advisor-angel-agreements">full agreement text</Link>{" "}
        is available on the Legal page.
      </p>

      <table>
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Instrument</td>
            <td>
              Convertible capital loan (Finnish Companies Act, Chapter 12)
            </td>
          </tr>
          <tr>
            <td>Conversion valuation</td>
            <td>EUR 5,000,000 post-money (fixed)</td>
          </tr>
          <tr>
            <td>Conversion rate</td>
            <td>EUR 25,000 = 0.5% on fully diluted basis</td>
          </tr>
          <tr>
            <td>Total angel allocation</td>
            <td>5-10% (EUR 250k-500k at EUR 5M)</td>
          </tr>
          <tr>
            <td>Committed to date</td>
            <td>EUR 150k+</td>
          </tr>
          <tr>
            <td>Conversion trigger</td>
            <td>Automatic upon Qualified Investment Round (EUR 500k+ aggregate)</td>
          </tr>
          <tr>
            <td>Conversion timing</td>
            <td>Pre-money of VC round</td>
          </tr>
          <tr>
            <td>Anti-dilution</td>
            <td>Full ratchet if QIR valuation below EUR 5M</td>
          </tr>
        </tbody>
      </table>

      <Callout variant="info" title="How CLAs Convert">
        All CLAs convert simultaneously, immediately before the VC round
        closes. Each EUR 25,000 becomes 0.5% of the company on a fully
        diluted basis at a fixed EUR 5M post-money valuation. The conversion
        happens pre-money of the VC round — meaning angels are on the cap
        table before new investors come in. All angels convert at the same
        price regardless of when they invested.
      </Callout>

      <hr />

      <h2>Scenario: Post-Angel Conversion + Pre-Seed</h2>
      <p>
        The scenario below assumes 7.5% total angel dilution (EUR 375k
        invested at EUR 5M post-money) and a EUR 2M pre-seed at EUR 20M
        pre-money.
      </p>

      <h3>Step 1: Remaining CLAs convert (pre-money of VC round)</h3>
      <p>
        One angel (0.5%) has already converted. Remaining CLAs bring total
        angel ownership to 7.5%. New shares are issued; founders dilute
        proportionally.
      </p>

      <table>
        <thead>
          <tr>
            <th>Stakeholder</th>
            <th>Current</th>
            <th>Post-conversion</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Founders (4x equal)</td>
            <td>99.5% (~24.9% each)</td>
            <td>92.5% (~23.1% each)</td>
          </tr>
          <tr>
            <td>Advisor angels (total)</td>
            <td>0.5%</td>
            <td>7.5%</td>
          </tr>
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td>
              <strong>100.0%</strong>
            </td>
            <td>
              <strong>100.0%</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Step 2: Pre-seed closes (EUR 2M at EUR 20M pre-money)</h3>
      <table>
        <thead>
          <tr>
            <th>Stakeholder</th>
            <th>Post-conversion</th>
            <th>Post-pre-seed</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Founders (4x equal)</td>
            <td>92.5%</td>
            <td>84.1% (~21.0% each)</td>
          </tr>
          <tr>
            <td>Advisor angels</td>
            <td>7.5%</td>
            <td>6.8%</td>
          </tr>
          <tr>
            <td>VC Pre-seed</td>
            <td>—</td>
            <td>9.1%</td>
          </tr>
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td>
              <strong>100.0%</strong>
            </td>
            <td>
              <strong>100.0%</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <OwnershipChart
        title="Post-Angel Conversion + Pre-Seed"
        data={[
          { name: "Founders (4x equal)", value: 84.1 },
          { name: "Advisor Angels", value: 6.8 },
          { name: "VC Pre-seed", value: 9.1 },
        ]}
      />

      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Pre-money valuation</td>
            <td>EUR 20,000,000</td>
          </tr>
          <tr>
            <td>Investment</td>
            <td>EUR 2,000,000</td>
          </tr>
          <tr>
            <td>Post-money valuation</td>
            <td>EUR 22,000,000</td>
          </tr>
          <tr>
            <td>VC ownership</td>
            <td>9.1%</td>
          </tr>
          <tr>
            <td>Founder ownership (combined)</td>
            <td>84.1%</td>
          </tr>
          <tr>
            <td>Price per percent</td>
            <td>~EUR 220,000</td>
          </tr>
        </tbody>
      </table>

      <Callout variant="tip" title="Employee Option Pool">
        An employee option pool (ESOP) is expected to be established as part
        of the pre-seed round negotiation. Pool size and structure will be
        agreed with incoming investors. This is standard practice — most
        VCs prefer the pool to be created from pre-money, ensuring the
        dilution is shared rather than falling solely on existing
        shareholders.
      </Callout>

      <hr />

      <h2>Illustrative: Post-Seed</h2>

      <Callout variant="warning" title="Forward-Looking">
        The following is illustrative only. Seed round terms, timing, and
        structure are not finalized. Shown for dilution modeling purposes.
      </Callout>

      <p>
        Assumes: EUR 10M at EUR 100M pre-money (EUR 110M post-money, 9.1%
        dilution). Targeted Winter 2026-27. Does not include potential ESOP
        top-up at seed.
      </p>

      <table>
        <thead>
          <tr>
            <th>Stakeholder</th>
            <th>Post-pre-seed</th>
            <th>Post-seed</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Founders (4x equal)</td>
            <td>84.1%</td>
            <td>76.5% (~19.1% each)</td>
          </tr>
          <tr>
            <td>Advisor angels</td>
            <td>6.8%</td>
            <td>6.2%</td>
          </tr>
          <tr>
            <td>VC Pre-seed</td>
            <td>9.1%</td>
            <td>8.3%</td>
          </tr>
          <tr>
            <td>VC Seed</td>
            <td>—</td>
            <td>9.1%</td>
          </tr>
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td>
              <strong>100.0%</strong>
            </td>
            <td>
              <strong>100.0%</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <OwnershipChart
        title="Illustrative Post-Seed"
        data={[
          { name: "Founders (4x equal)", value: 76.5 },
          { name: "Advisor Angels", value: 6.2 },
          { name: "VC Pre-seed", value: 8.3 },
          { name: "VC Seed", value: 9.1 },
        ]}
      />

      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Pre-money valuation</td>
            <td>EUR 100,000,000</td>
          </tr>
          <tr>
            <td>Investment</td>
            <td>EUR 10,000,000</td>
          </tr>
          <tr>
            <td>Post-money valuation</td>
            <td>EUR 110,000,000</td>
          </tr>
          <tr>
            <td>Pre-seed investor value</td>
            <td>EUR 9,130,000 (4.6x on EUR 2M)</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>Dilution Waterfall</h2>
      <p>How each stakeholder group evolves across rounds:</p>

      <table>
        <thead>
          <tr>
            <th>Stakeholder</th>
            <th>Current</th>
            <th>Post-angels</th>
            <th>Post-pre-seed</th>
            <th>Post-seed</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Founders (4x)</td>
            <td>99.5%</td>
            <td>92.5%</td>
            <td>84.1%</td>
            <td>76.5%</td>
          </tr>
          <tr>
            <td>Each founder</td>
            <td>24.875%</td>
            <td>23.1%</td>
            <td>21.0%</td>
            <td>19.1%</td>
          </tr>
          <tr>
            <td>Advisor angels</td>
            <td>0.5%</td>
            <td>7.5%</td>
            <td>6.8%</td>
            <td>6.2%</td>
          </tr>
          <tr>
            <td>VC Pre-seed</td>
            <td>—</td>
            <td>—</td>
            <td>9.1%</td>
            <td>8.3%</td>
          </tr>
          <tr>
            <td>VC Seed</td>
            <td>—</td>
            <td>—</td>
            <td>—</td>
            <td>9.1%</td>
          </tr>
        </tbody>
      </table>

      <Callout variant="tip" title="Founder Retention">
        After three rounds of financing (angels, pre-seed, seed), the four
        founders retain over 76% of the company combined. Each individual
        founder holds ~19%. This is strong founder retention for a company
        that has raised EUR 12M+ in equity. An ESOP established at pre-seed
        or seed will reduce these percentages — typically by 8-12% shared
        across all stakeholders.
      </Callout>

      <Details variant="info" title="Sensitivity: Angel Dilution Range">
        <p>
          The angel allocation is 5-10%. Here is how the post-pre-seed cap
          table changes at different angel participation levels:
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Angel %</TableHead>
              <TableHead>Founders post-pre-seed</TableHead>
              <TableHead>Angels post-pre-seed</TableHead>
              <TableHead>VC post-pre-seed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">5.0%</TableCell>
              <TableCell>86.4%</TableCell>
              <TableCell>4.5%</TableCell>
              <TableCell>9.1%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">7.5%</TableCell>
              <TableCell>84.1%</TableCell>
              <TableCell>6.8%</TableCell>
              <TableCell>9.1%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">10.0%</TableCell>
              <TableCell>81.8%</TableCell>
              <TableCell>9.1%</TableCell>
              <TableCell>9.1%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Details>

      <hr />

      <h2>Key Terms</h2>

      <Details variant="note" title="Conversion Mechanics">
        <ul>
          <li>
            All CLAs convert simultaneously at Qualified Investment Round
            (EUR 500k+ aggregate new investment)
          </li>
          <li>
            Fixed EUR 5M post-money valuation for all angels (regardless of
            investment timing)
          </li>
          <li>Conversion happens pre-money of VC round</li>
          <li>Angels receive same share class as VC round investors</li>
          <li>
            Full ratchet anti-dilution if VC round valuation below EUR 5M
          </li>
          <li>
            MFN clause: if any CLA gets better terms, all angels can request
            the same
          </li>
        </ul>
      </Details>

      <Details variant="note" title="Employee Option Pool (Planned)">
        <ul>
          <li>No ESOP currently exists</li>
          <li>
            Expected to be established as part of pre-seed round
            negotiation
          </li>
          <li>Typical size: 8-12% from pre-money</li>
          <li>
            Will be used for key hires, future advisors, and employee
            incentives
          </li>
          <li>Structure and size to be agreed with incoming investors</li>
        </ul>
      </Details>

      <Details variant="note" title="Founder Vesting">
        <ul>
          <li>
            Standard 4-year vesting with 1-year cliff, monthly thereafter
          </li>
          <li>All founders are full-time from day one</li>
          <li>Shares held through personal holding companies</li>
          <li>
            Good leaver / bad leaver provisions in shareholders' agreement
            (being finalized with Hannes Snellman)
          </li>
        </ul>
      </Details>
    </>
  );
}
